import React from 'react';

import {Button, Container, Paper, Stack, Typography} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {defineMessages, useIntl} from 'react-intl';
import {useMutation} from 'react-query';

import API, {Types, useAuth, useCsrfToken} from '@wheeebo/polaris-api';
import {useSnackbar} from '@wheeebo/polaris-snackbar';
import {CircleLoader, useErrorHandler} from '@wheeebo/polaris-ui';

const msgs = defineMessages({
  headerTitle: {
    id: 'pages.member.VehicleActivatePage.header.title',
    defaultMessage: 'Vehicle Activation Confirmation',
  },
  buttonBackToTopPage: {
    id: 'pages.member.VehicleActivatePage.button.backToTopPage',
    defaultMessage: 'Back to the top page',
  },
  textVerified: {
    id: 'pages.member.VehicleActivatePage.text.verified',
    defaultMessage: 'Your vehicle was successfully activated.',
  },
  textVerificationFailed: {
    id: 'pages.member.VehicleActivatePage.text.activationFailed',
    defaultMessage: 'The activation failed.',
  },
  successActivated: {
    id: 'pages.member.VehicleActivatePage.success.activated',
    defaultMessage: 'Activation confirmed',
  },
});

interface ConfirmationProps {
  vehicleId: Types.UUID;
  token: Types.UUID;
}

function VehicleActivatePage(): JSX.Element {
  const intl = useIntl();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {showAndEmitJsErrorEvent} = useErrorHandler();
  const {showSuccess} = useSnackbar();
  const {signOut} = useAuth();
  const {csrfToken} = useCsrfToken();
  const handleGoBack = async () => {
    await signOut?.();
    navigate('/');
  };
  const {mutate, isLoading, isSuccess, isError} = useMutation<unknown, unknown, ConfirmationProps>(
    (props: ConfirmationProps) => API.Vehicles.confirmActivation(csrfToken, props.vehicleId, props.token),
    {
      onSuccess: () => {
        showSuccess(intl.formatMessage(msgs.successActivated));
      },
      onError: showAndEmitJsErrorEvent,
    },
  );

  React.useEffect(() => {
    if (params.vehicleId && searchParams.has('token')) {
      mutate({vehicleId: params.vehicleId, token: searchParams.get('token') || ''});
    }
  }, [params.vehicleId, searchParams, mutate]);

  return (
    <Container component="main" sx={{display: 'flex', justifyContent: 'center'}}>
      <Paper sx={{width: 400, mt: 8, p: 3}}>
        <Stack gap={3}>
          <Typography variant="h6" align="center">
            {intl.formatMessage(msgs.headerTitle)}
          </Typography>
          {isLoading ? <CircleLoader /> : null}
          {isSuccess ? <Typography variant="body1">{intl.formatMessage(msgs.textVerified)}</Typography> : null}
          {isError ? <Typography variant="body1">{intl.formatMessage(msgs.textVerificationFailed)}</Typography> : null}
          <Button onClick={handleGoBack} startIcon={<ArrowBackIcon />}>
            {intl.formatMessage(msgs.buttonBackToTopPage)}
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}

export default React.memo(VehicleActivatePage);
